import moment from 'moment'

// The backend translator (FmcsaTranslatorService) turns missing values into 'n/a' / 'None',
// maps Y/N to Yes/No and formats BIPD amounts as "$750,000". Everything else arrives raw.
const MISSING = ['', '-', 'n/a', 'none', 'null']

export const NOT_REPORTED = 'Not reported'

const NAMED_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' }

// FMCSA text can carry HTML entities and zero-width spaces ("Controlled Substances/&#8203;Alcohol").
const cleanText = text => text
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(Number(dec)))
    .replace(/&([a-z]+);/gi, (m, name) => NAMED_ENTITIES[name.toLowerCase()] ?? m)
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim()

export const present = value => {
    if (value === null || value === undefined) return null
    const text = cleanText(String(value))
    return MISSING.includes(text.toLowerCase()) ? null : text
}

const orNotReported = value => present(value) ?? NOT_REPORTED

const yesNo = value => {
    const v = present(value)?.toLowerCase()
    if (v === 'yes' || v === 'y') return 'Yes'
    if (v === 'no' || v === 'n') return 'No'
    return null
}

export const formatDate = value => {
    const v = present(value)
    if (!v) return null
    const parsed = moment(v, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true)
    return parsed.isValid() ? parsed.format('MMM D, YYYY') : v
}

const money = amount => `$${amount.toLocaleString('en-US')}`

// BIPD comes formatted ("$1,000,000"); bond and cargo arrive raw in thousands of dollars ("75").
const parseFormattedAmount = value => {
    const v = present(value)
    if (!v) return null
    const n = Number(v.replace(/[$,]/g, ''))
    return Number.isFinite(n) ? n : null
}

const parseThousands = value => {
    const v = present(value)
    if (!v) return null
    const n = Number(v.replace(/,/g, ''))
    return Number.isFinite(n) ? n * 1000 : null
}

const formatEin = value => {
    const v = present(value)
    if (!v || v === '0') return null
    const digits = v.padStart(9, '0')
    return digits.length === 9 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : v
}

const AUTHORITY_STATUS = {
    a: { label: 'Active', tone: 'approved', active: true },
    active: { label: 'Active', tone: 'approved', active: true },
    i: { label: 'Inactive', tone: 'watch' },
    inactive: { label: 'Inactive', tone: 'watch' },
    n: { label: 'None', tone: 'closed' },
    no: { label: 'None', tone: 'closed' },
}

const authorityStatus = value => {
    const v = present(value)
    if (!v) return null
    return AUTHORITY_STATUS[v.toLowerCase()] || { label: v, tone: 'closed' }
}

const SAFETY_RATING = {
    satisfactory: { label: 'Satisfactory', tone: 'approved' },
    s: { label: 'Satisfactory', tone: 'approved' },
    conditional: { label: 'Conditional', tone: 'watch' },
    c: { label: 'Conditional', tone: 'watch' },
    unsatisfactory: { label: 'Unsatisfactory', tone: 'declined' },
    u: { label: 'Unsatisfactory', tone: 'declined' },
}

const safetyRating = value => {
    const v = present(value)
    return (v && SAFETY_RATING[v.toLowerCase()]) || { label: 'Not rated', tone: 'closed' }
}

const insuranceRow = ({ key, name, full, required, onFile, requiredAmount }) => {
    const isRequired = required === 'Yes'
    const hasRequiredAmount = isRequired && requiredAmount != null && requiredAmount > 0
    const short = isRequired && (hasRequiredAmount ? (onFile ?? 0) < requiredAmount : !onFile)

    let badge = null
    let note = null
    if (isRequired) {
        if (!onFile) badge = { label: 'No coverage on file', tone: 'declined' }
        else if (short) badge = { label: 'Below requirement', tone: 'declined' }
        else badge = { label: 'Meets requirement', tone: 'approved' }

        if (hasRequiredAmount) {
            const diff = (onFile ?? 0) - requiredAmount
            note = diff === 0 ? 'Matches required amount'
                : diff > 0 ? `${money(diff)} above required`
                    : `Short by ${money(-diff)}`
        }
    }

    return {
        key,
        name,
        full,
        required: required ?? NOT_REPORTED,
        onFile: onFile == null ? NOT_REPORTED : money(onFile),
        requiredAmount: hasRequiredAmount ? money(requiredAmount) : null,
        isRequired,
        short,
        badge,
        note,
        // Bar only where FMCSA publishes a required amount (BIPD); scale so the tick sits at 75%.
        bar: hasRequiredAmount ? {
            fill: Math.min(100, ((onFile ?? 0) / requiredAmount) * 75),
            tick: 75,
        } : null,
        shortfallText: !short ? null
            : hasRequiredAmount ? `${name}: ${money(onFile ?? 0)} on file, ${money(requiredAmount)} required`
                : `${name}: required, none on file`,
    }
}

const insuranceVerdict = rows => {
    const required = rows.filter(r => r.isRequired)
    const reported = rows.filter(r => r.required !== NOT_REPORTED)
    if (reported.length === 0) {
        return { badge: NOT_REPORTED, tone: 'closed', detail: 'FMCSA returned no insurance requirements' }
    }
    if (required.length === 0) {
        return { badge: 'No coverage required', tone: 'closed', detail: 'FMCSA lists no required BIPD, cargo or bond coverage' }
    }
    const short = required.filter(r => r.short)
    if (short.length === 0) {
        return { badge: 'Meets requirements', tone: 'approved', detail: 'Every required policy is on file at or above the minimum' }
    }
    return {
        badge: `Short on ${short.length} ${short.length === 1 ? 'policy' : 'policies'}`,
        tone: 'declined',
        detail: short[0].shortfallText,
    }
}

const joinTypes = types => types.length <= 1 ? types.join('')
    : `${types.slice(0, -1).join(', ')} and ${types[types.length - 1]}`

const authorityVerdict = authority => {
    const reported = authority.filter(a => a.status)
    if (reported.length === 0) {
        return { badge: NOT_REPORTED, tone: 'closed', detail: 'FMCSA returned no authority status' }
    }
    const active = authority.filter(a => a.status?.active).map(a => a.label.toLowerCase())
    if (active.length === 0) {
        return { badge: 'None active', tone: 'watch', detail: 'No active common, contract or broker authority' }
    }
    return { badge: 'Active', tone: 'approved', detail: `Active ${joinTypes(active)} authority` }
}

export const buildSummary = (carrier, mcNumbers) => {
    // The translator turns a missing DOT into 0.
    const dot = present(carrier?.dotNumber) === '0' ? null : present(carrier?.dotNumber)
    if (!carrier || !(present(carrier.legalName) || dot)) return null

    const mc = Array.isArray(mcNumbers) ? mcNumbers.filter(Boolean).join(', ') : present(mcNumbers)
    const ein = formatEin(carrier.ein)
    const address = [
        present(carrier.phyStreet),
        present(carrier.phyCity),
        [present(carrier.phyState), present(carrier.phyZipcode)].filter(Boolean).join(' '),
    ].filter(Boolean).join(', ')

    const allowed = yesNo(carrier.allowedToOperate)
    const rating = safetyRating(carrier.safetyRating)
    const ratingDate = formatDate(carrier.safetyRatingDate)

    const insurance = [
        insuranceRow({
            key: 'bipd',
            name: 'BIPD',
            full: 'Bodily injury and property damage liability',
            required: yesNo(carrier.bipdInsuranceRequired),
            onFile: parseFormattedAmount(carrier.bipdInsuranceOnFile),
            requiredAmount: parseFormattedAmount(carrier.bipdRequiredAmount),
        }),
        insuranceRow({
            key: 'cargo',
            name: 'Cargo',
            full: 'Cargo insurance',
            required: yesNo(carrier.cargoInsuranceRequired),
            onFile: parseThousands(carrier.cargoInsuranceOnFile),
        }),
        insuranceRow({
            key: 'bond',
            name: 'Bond',
            full: 'Surety bond or trust fund',
            required: yesNo(carrier.bondInsuranceRequired),
            onFile: parseThousands(carrier.bondInsuranceOnFile),
        }),
    ]

    const authority = [
        { label: 'Common', status: authorityStatus(carrier.commonAuthorityStatus) },
        { label: 'Contract', status: authorityStatus(carrier.contractAuthorityStatus) },
        { label: 'Broker', status: authorityStatus(carrier.brokerAuthorityStatus) },
    ]

    const hazmat = present(carrier.hazmatOosRateNationalAverage)

    return {
        legalName: orNotReported(carrier.legalName),
        dba: present(carrier.dbaName),
        dot,
        chips: [
            { label: 'DOT', value: dot || NOT_REPORTED },
            { label: 'MC', value: mc || NOT_REPORTED },
            { label: 'EIN', value: ein || NOT_REPORTED },
        ],
        verdicts: [
            {
                label: 'Allowed to operate',
                badge: allowed || NOT_REPORTED,
                tone: allowed === 'Yes' ? 'approved' : allowed === 'No' ? 'declined' : 'closed',
                detail: allowed === 'Yes' ? 'FMCSA lists the carrier as authorized'
                    : allowed === 'No' ? 'FMCSA does not authorize this carrier to operate'
                        : 'FMCSA returned no operating status',
            },
            {
                label: 'Safety rating',
                badge: rating.label,
                tone: rating.tone,
                detail: rating.tone !== 'closed' && ratingDate ? `Rated ${ratingDate}` : 'No rating on file',
            },
            { label: 'Insurance', ...insuranceVerdict(insurance) },
            { label: 'Operating authority', ...authorityVerdict(authority) },
        ],
        business: [
            { label: 'Legal name', value: orNotReported(carrier.legalName) },
            { label: 'DBA', value: orNotReported(carrier.dbaName) },
            { label: 'Physical address', value: address || NOT_REPORTED },
            { label: 'EIN', value: ein || NOT_REPORTED },
        ],
        authority,
        operation: [
            { label: 'Operation code', value: orNotReported(carrier.carrierOperation?.carrierOperationCode) },
            { label: 'Operation', value: orNotReported(carrier.carrierOperation?.carrierOperationDesc) },
        ],
        insurance,
        rating,
        safety: [
            { label: 'Rating date', value: ratingDate || NOT_REPORTED },
            { label: 'Review date', value: formatDate(carrier.safetyReviewDate) || NOT_REPORTED },
            { label: 'Review type', value: orNotReported(carrier.safetyReviewType) },
        ],
        crashes: [
            { label: 'Fatal crashes', value: carrier.fatalCrash ?? NOT_REPORTED },
            { label: 'Injury crashes', value: carrier.injCrash ?? NOT_REPORTED },
            { label: 'Total crashes', value: carrier.crashTotal ?? NOT_REPORTED },
        ],
        hazmat: hazmat ? `${hazmat}%` : NOT_REPORTED,
        fleet: [
            { label: 'Total drivers', value: carrier.totalDrivers ?? NOT_REPORTED },
            { label: 'Total power units', value: carrier.totalPowerUnits ?? NOT_REPORTED },
        ],
    }
}

// ---------- Extensive search ----------

const toNumber = value => {
    const v = present(value)
    if (!v) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

const sentenceCase = text => text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : text

// FMCSA sends both abbreviations ("HOS Compliance", "Vehicle Maint.") and full names
// ("Hours-of-Service Compliance"); match on letters only.
const BASIC_NAMES = {
    unsafedriving: 'Unsafe driving',
    hoscompliance: 'Hours-of-service compliance',
    hoursofservicecompliance: 'Hours-of-service compliance',
    driverfitness: 'Driver fitness',
    drugsalcohol: 'Controlled substances and alcohol',
    controlledsubstancesalcohol: 'Controlled substances and alcohol',
    controlledsubstancesandalcohol: 'Controlled substances and alcohol',
    vehiclemaint: 'Vehicle maintenance',
    vehiclemaintenance: 'Vehicle maintenance',
    hmcompliance: 'Hazardous materials compliance',
    hazmatcompliance: 'Hazardous materials compliance',
    hazardousmaterialscompliance: 'Hazardous materials compliance',
    crashindicator: 'Crash indicator',
}

const basicName = type => {
    const raw = present(type?.basicsShortDesc) || present(type?.basicsCode) || 'BASIC'
    return BASIC_NAMES[raw.toLowerCase().replace(/[^a-z]/g, '')] || sentenceCase(raw)
}

const ACRONYMS = /\b(oos|dot|mc|bipd|hm|hos|us)\b/gi

const humanizeKey = key => sentenceCase(
    key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').trim()
).replace(ACRONYMS, m => m.toUpperCase())

const renderScalar = value => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    return yesNo(value) || formatDate(value) || orNotReported(value)
}

// The OOS payload has no fixed schema: flatten whatever comes back into label/value rows.
const flattenOos = oos => {
    const rows = []
    const visit = (obj, prefix) => {
        Object.entries(obj || {}).forEach(([key, value]) => {
            if (['retrievalDate', 'metrics'].includes(key) || value === null || value === undefined || value === '') return
            if (key === 'additionalProperties' || key === 'content') return visit(value, prefix)
            if (Array.isArray(value)) {
                value.forEach(item => typeof item === 'object' ? visit(item, prefix)
                    : rows.push({ label: humanizeKey(prefix || key), value: renderScalar(item) }))
                return
            }
            if (typeof value === 'object') return visit(value, key)
            rows.push({ key, label: humanizeKey(key), value: renderScalar(value) })
        })
    }
    visit(oos)
    return rows
}

export const buildExtensive = data => {
    if (!data) return null

    const mcRows = (data.mcNumbersResponse?.content || []).map(m => ({
        docket: orNotReported(m.docketNumber),
        dot: orNotReported(m.dotNumber),
        prefix: orNotReported(m.prefix),
    }))
    if (mcRows.length === 0 && Array.isArray(data.mcNumbers)) {
        data.mcNumbers.filter(Boolean).forEach(n => mcRows.push({ docket: n, dot: NOT_REPORTED, prefix: 'MC' }))
    }

    const authorities = (data.authorityResponse?.content || [])
        .map(item => item.carrierAuthority)
        .filter(Boolean)
        .map(a => ({
            status: authorityStatus(a.authority)?.label || NOT_REPORTED,
            docket: present(a.docketNumber) ? `${present(a.prefix) || 'MC'}-${a.docketNumber}` : NOT_REPORTED,
            authorizedFor: [
                { label: 'Broker', value: yesNo(a.authorizedForBroker) },
                { label: 'Household goods', value: yesNo(a.authorizedForHouseholdGoods) },
                { label: 'Passenger', value: yesNo(a.authorizedForPassenger) },
                { label: 'Property', value: yesNo(a.authorizedForProperty) },
            ],
        }))

    const basics = (data.basicResponse?.content || [])
        .map(item => item.basic)
        .filter(Boolean)
        .map(b => {
            const percentile = toNumber(b.basicsPercentile)
            const threshold = toNumber(b.basicsViolationThreshold)
            const flag = present(b.exceededFMCSAInterventionThreshold)?.toUpperCase()
            const exceeded = flag === 'Y' || (flag !== '-1' && percentile != null && threshold != null && percentile >= threshold)
            return {
                name: basicName(b.basicsType),
                percentile,
                threshold,
                exceeded,
                rows: [
                    { label: 'Measure', value: orNotReported(b.measureValue) },
                    { label: 'Inspections with violations', value: b.totalInspectionWithViolation ?? NOT_REPORTED },
                    { label: 'Total violations', value: b.totalViolation ?? NOT_REPORTED },
                    {
                        label: 'Serious violation, past 12 months',
                        value: yesNo(b.seriousViolationFromInvestigationPast12MonthIndicator) || NOT_REPORTED,
                        alert: yesNo(b.seriousViolationFromInvestigationPast12MonthIndicator) === 'Yes',
                    },
                ],
            }
        })

    basics.sort((a, b) => (b.exceeded - a.exceeded)
        || ((b.percentile ?? -1) - (a.percentile ?? -1)))

    const exceededCount = basics.filter(b => b.exceeded).length
    const withPercentile = basics.filter(b => b.percentile != null).length
    const unpublished = basics.length - withPercentile

    const oosRows = flattenOos(data.oosContent)
    const oosDate = oosRows.find(r => /oos.?date|out.?of.?service.?date/i.test(r.key || ''))

    return {
        mcRows,
        authorities,
        basics,
        // No published percentile anywhere: the drawer shows a compact table instead of gauge cards
        basicsCompact: basics.length > 0 && withPercentile === 0,
        basicsSummary: basics.length === 0 ? null
            : withPercentile === 0 ? 'FMCSA has not published percentiles for any category. Measures and violation counts are shown as reported.'
                : [
                    exceededCount === 0 ? 'No category is over its intervention threshold.'
                        : `${exceededCount} of ${basics.length} ${basics.length === 1 ? 'category' : 'categories'} over the intervention threshold.`,
                    unpublished > 0 ? `${unpublished} ${unpublished === 1 ? 'has' : 'have'} no published percentile and ${unpublished === 1 ? 'is' : 'are'} listed last.` : null,
                    'The tick on each gauge marks the threshold.',
                ].filter(Boolean).join(' '),
        cargo: (data.cargoCarriedResponse?.content || []).map(c => present(c.cargoClassDesc)).filter(Boolean),
        operations: (data.operationClassificationResponse?.content || []).map(o => present(o.operationClassDesc)).filter(Boolean),
        oosRows,
        oosBadge: oosDate && oosDate.value !== NOT_REPORTED ? { label: 'Out of service', tone: 'declined' }
            : oosRows.length === 0 ? { label: NOT_REPORTED, tone: 'closed' }
                : null,
    }
}
